import { getComplianceSummary } from '@/api/dashboardApi'
import { globalComplianceFootprintUIMetaData } from '@/constants/dashboardConstant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography } from '@mui/material'
import { geoCentroid, geoMercator, geoPath } from 'd3-geo'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps'

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json'

// Default (empty countries) shape used before the footprint payload loads or
// when the response is missing/malformed.
const defaultGlobalComplianceFootprintData = {
  total: '',
  countries: [],
}

// ISO 3166-1 country-code lookup. The atlas identifies each geography by its
// numeric code (as a string, e.g. "356"), while the API returns either alpha-2
// ("IN") or alpha-3 ("SGP"). This table maps BOTH alpha forms to the numeric
// code so a highlighted country can be matched regardless of which the API
// sends. Extend it as more countries are onboarded.
const COUNTRY_CODE_TO_NUMERIC = {
  IND: '356',
  SGP: '702',
  SGD: '702',
  EGX: '818',
  COL: '170',
}

// Projection config — MUST mirror the <ComposableMap> props below so projected
// pixel-area measurements match what the user actually sees.
//
// The SVG viewBox is fixed at MAP_WIDTH x MAP_HEIGHT and scales UNIFORMLY to
// fill the width of a container locked to the same aspect ratio, so the map
// looks identical at every screen size.
//
// Calibrated to the design: the WHOLE world is visible inside the card with a
// small uniform margin (preserveAspectRatio="meet"), centered, framed from
// just above northern Canada/Russia (top) to just past South America/Australia
// (bottom), with the full eastern edge of Russia and the Pacific in view.
// - SCALE 125 makes the full 360° Mercator world fill the 800px viewBox width
//   (800 / 2π ≈ 127), so the antimeridian sits at both edges and Russia's far
//   eastern tip and Alaska are both fully visible — not clipped.
// - CENTER longitude 0 keeps the world symmetric (standard world-map framing);
//   latitude 42 sets the top/bottom crop to match the design.
const MAP_WIDTH = 800
const MAP_HEIGHT = 560
const PROJECTION_SCALE = 125
const PROJECTION_CENTER = [14, 42]
// A highlighted country is only given a marker dot when its rendered landmass
// is smaller than this many square pixels — large countries (e.g. India) are
// visible on their own and don't need one, while tiny nations (e.g. Singapore)
// would otherwise be invisible.
const SMALL_COUNTRY_AREA_THRESHOLD = 40

// Radius (in map pixels) of the marker dot drawn for small highlighted
// countries. Bump this up to make tiny nations easier to spot.
const SMALL_COUNTRY_MARKER_RADIUS = 6

// Fill/marker color for highlighted countries. The API response carries no
// per-country color, so a single brand color is used for all of them.
const HIGHLIGHT_COLOR = 'var(--forbrand-color)'

// ==================== Helpers ====================
// Builds a path measurer using the same projection as the rendered map so the
// projected area of a geography can be compared against the pixel threshold.
const createPathMeasurer = () => {
  const projection = geoMercator()
    .scale(PROJECTION_SCALE)
    .center(PROJECTION_CENTER)
    .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2])
  return geoPath().projection(projection)
}

// True when the geography renders smaller than the threshold (i.e. too small to
// see without a marker). Falls back to treating it as small if area can't be
// measured, so a highlighted country is never silently hidden.
const isSmallOnMap = (pathMeasurer, geo) => {
  const area = pathMeasurer.area(geo)
  if (!Number.isFinite(area)) {
    return true
  }
  return area < SMALL_COUNTRY_AREA_THRESHOLD
}

// Normalizes the API's country list into a map keyed by the atlas numeric code
// so each geography can be matched in O(1) during render. Accepts either alpha-2
// or alpha-3 countryCode; drops entries whose code isn't in the lookup.
const buildCountryMapByNumericId = (countries = []) => {
  const map = {}
  countries.forEach(country => {
    const numericId = COUNTRY_CODE_TO_NUMERIC[country?.countryCode]
    if (numericId) {
      map[numericId] = country
    }
  })
  return map
}

// Merges the compliance-summary API payload ({ total, countries }) into the
// footprint shape. Returns the default (empty countries) shape when the payload
// is missing.
const mapGlobalComplianceFootprintToData = (data = {}) => ({
  total: data?.total ?? '',
  countries: Array.isArray(data?.countries) ? data.countries : [],
})

const GlobalComplianceFootprint = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [tooltipContent, setTooltipContent] = useState('')
  const [data, setData] = useState(defaultGlobalComplianceFootprintData)
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Derived Data ====================
  // Atlas-numeric-id → country record, so each geography is matched in O(1).
  const countryMap = buildCountryMapByNumericId(data.countries)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const complianceSummaryResponse =
          await getComplianceSummary(appliedFilters)
        processData(complianceSummaryResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Handlers ====================
  const handleMouseEnter = geo => {
    const country = countryMap[geo.id]
    if (country) {
      setTooltipContent(`${country.countryName}:  ${country.total}`)
    }
  }

  const handleMouseLeave = () => {
    setTooltipContent('')
  }

  // ==================== Component Helpers ====================
  const processData = complianceSummaryResponse => {
    const complianceSummaryData = complianceSummaryResponse?.data?.data
    if (complianceSummaryResponse?.success && complianceSummaryData) {
      setData(mapGlobalComplianceFootprintToData(complianceSummaryData))
    } else {
      setData(defaultGlobalComplianceFootprintData)
    }
  }

  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        bgcolor: 'var(--white-color)',
        p: 2.5,
        flex: '1 1 0',
        minWidth: '300px',
        // Fixed height matches the sibling dashboard cards
        // (CompliancePerformanceOverview / WHTVarianceSummary / ComplianceOverview)
        // so the whole row is equal height. The map below fills the leftover
        // space and scales to fit (preserveAspectRatio="meet").
        height: '323px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            lineHeight: 1.4,
          }}
        >
          {globalComplianceFootprintUIMetaData.heading}
        </Typography>

        <Tooltip title={globalComplianceFootprintUIMetaData.subHeading}>
          <InfoOutlinedIcon sx={{ width: '17.5px', height: '17.47px' }} />
        </Tooltip>
      </Box>

      {/* Map */}
      {!dataLoaded ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-lg)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            // Fill the remaining card height below the header. The map SVG
            // scales to fit this area (preserveAspectRatio="meet"), so the card
            // stays at the fixed 323px height and matches its siblings while the
            // world map still renders consistently across screen sizes.
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        >
          <Tooltip title={tooltipContent} arrow followCursor placement="top">
            {/* Fills the flex area above; the SVG scales uniformly to fit
                (preserveAspectRatio="meet") so the whole world stays visible. */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <ComposableMap
                width={MAP_WIDTH}
                height={MAP_HEIGHT}
                projection="geoMercator"
                projectionConfig={{
                  scale: PROJECTION_SCALE,
                  center: PROJECTION_CENTER,
                }}
                style={{ width: '100%', height: '100%' }}
                preserveAspectRatio="xMidYMid meet"
              >
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => {
                    // Measure each highlighted country once and keep only those
                    // too small to see — they get a marker dot after the shapes.
                    const pathMeasurer = createPathMeasurer()
                    const smallHighlighted = geographies.filter(
                      geo =>
                        !!countryMap[geo.id] && isSmallOnMap(pathMeasurer, geo)
                    )
                    return (
                      <>
                        {geographies.map(geo => {
                          const isHighlighted = !!countryMap[geo.id]
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={
                                isHighlighted
                                  ? HIGHLIGHT_COLOR
                                  : 'var(--map-grey)'
                              }
                              stroke="var(--white-color)"
                              strokeWidth={0.5}
                              style={{
                                default: { outline: 'none' },
                                hover: {
                                  outline: 'none',
                                  cursor: isHighlighted ? 'pointer' : 'default',
                                },
                                pressed: { outline: 'none' },
                              }}
                              onMouseEnter={() => handleMouseEnter(geo)}
                              onMouseLeave={handleMouseLeave}
                            />
                          )
                        })}
                        {smallHighlighted.map(geo => (
                          <Marker
                            key={`${geo.rsmKey}-marker`}
                            coordinates={geoCentroid(geo)}
                          >
                            <circle
                              r={SMALL_COUNTRY_MARKER_RADIUS}
                              fill={HIGHLIGHT_COLOR}
                              stroke="var(--white-color)"
                              strokeWidth={1}
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={() => handleMouseEnter(geo)}
                              onMouseLeave={handleMouseLeave}
                            />
                          </Marker>
                        ))}
                      </>
                    )
                  }}
                </Geographies>
              </ComposableMap>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

export default GlobalComplianceFootprint
